import React, { useState, useEffect } from 'react';
import Navbar        from './components/Navbar';
import HomePage      from './components/HomePage';
import GamePage      from './components/GamePage';
import RegisterModal from './components/RegisterModal';
import { getPlayer, createPlayer, updateScore, getLeaderboard } from './supabase';

export default function App() {
  const [page,          setPage]          = useState('home');
  const [profile,       setProfile]       = useState(null);
  const [leaderboard,   setLeaderboard]   = useState([]);
  const [loadingLB,     setLoadingLB]     = useState(true);
  const [showAuth,      setShowAuth]      = useState(false);
  const [authTab,       setAuthTab]       = useState('login');
  const [loginError,    setLoginError]    = useState('');
  const [gameDuration,  setGameDuration]  = useState(5);
  const [soundSettings, setSoundSettings] = useState({
    bgm: true, sfx: true, collect: true, gameover: true,
  });

  useEffect(() => {
    getLeaderboard()
      .then(data => setLeaderboard(data))
      .finally(() => setLoadingLB(false));
  }, []);

  const openLogin    = () => { setAuthTab('login');    setLoginError(''); setShowAuth(true); };
  const openRegister = () => { setAuthTab('register'); setLoginError(''); setShowAuth(true); };

  const handleLogin = async (username, passkey, duration) => {
    if (!username.trim()) return setLoginError('Username is required!');
    if (!passkey.trim())  return setLoginError('Password is required!');
    const player = await getPlayer(username);
    if (!player)                    return setLoginError('Username not found! Please register first.');
    if (player.passkey !== passkey) return setLoginError('Wrong password! Try again.');
    setGameDuration(duration);
    setProfile({
      username:     player.username,
      passkey:      player.passkey,
      avatarId:     player.avatar_id,
      bestScore:    player.best_score    || 0,
      totalGames:   player.total_games   || 0,
      lastPlayed:   player.last_played   || '',
      timeSurvived: player.time_survived || 0,
      gameDuration: player.game_duration || 5,
    });
    setLoginError('');
    setShowAuth(false);
    setPage('game');
  };

  const handleRegister = async (data) => {
    if (!data.username.trim()) return setLoginError('Username is required!');
    if (!data.passkey.trim())  return setLoginError('Password is required!');
    const existing = await getPlayer(data.username);
    if (existing) {
      setAuthTab('register');
      return setLoginError('Username already taken! Choose another or login.');
    }
    const newPlayer = await createPlayer(data.username, data.passkey, data.avatarId);
    if (!newPlayer) return setLoginError('Something went wrong. Try again!');
    setGameDuration(data.duration);
    setProfile({
      username:     newPlayer.username,
      passkey:      newPlayer.passkey,
      avatarId:     newPlayer.avatar_id,
      bestScore:    0, totalGames: 0, lastPlayed: '',
      timeSurvived: 0, gameDuration: data.duration,
    });
    setLoginError('');
    setShowAuth(false);
    setPage('game');
    const lb = await getLeaderboard();
    setLeaderboard(lb);
  };

  const handleLogout = () => { setProfile(null); setPage('home'); };

  const handleGameOver = async (score, time) => {
    if (profile) {
      const updated = await updateScore(profile.username, score, time, gameDuration);
      if (updated) {
        setProfile(prev => ({
          ...prev,
          bestScore:    updated.best_score,
          totalGames:   updated.total_games,
          lastPlayed:   updated.last_played,
          gameDuration: updated.game_duration,
        }));
      }
      const lb = await getLeaderboard();
      setLeaderboard(lb);
    }
    setPage('home');
  };

  return (
    <>
      <Navbar
        profile={profile}
        onHome={() => setPage('home')}
        onRegister={openRegister}
        onProfile={openLogin}
        onLogout={handleLogout}
        soundSettings={soundSettings}
        setSoundSettings={setSoundSettings}
      />

      {page === 'home' && (
        <HomePage
          profile={profile}
          onPlay={() => profile ? setPage('game') : openLogin()}
          onLogin={openLogin}
          onRegister={openRegister}
          leaderboard={leaderboard}
          loadingLB={loadingLB}
        />
      )}

      {page === 'game' && (
        <GamePage
          profile={profile || { username: 'Guest', avatarId: 1 }}
          onBack={() => setPage('home')}
          onGameOver={handleGameOver}
          soundSettings={soundSettings}
          gameDuration={gameDuration}
        />
      )}

      {showAuth && (
        <RegisterModal
          onClose={() => { setShowAuth(false); setLoginError(''); }}
          onLogin={handleLogin}
          onRegister={handleRegister}
          loginError={loginError}
          startTab={authTab}
        />
      )}
    </>
  );
}